// Dashboard.js - Fixed version

import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Badge,
  ProgressBar,
} from "react-bootstrap";
import {
  FaUser,
  FaCreditCard,
  FaCog,
  FaMoneyBillWave,
  FaAd,
  FaStore,
  FaUsers,
  FaChartLine,
  FaEye,
  FaHandshake,
  FaShieldAlt,
  FaHome,
  FaUserCheck,
  FaFolder,
  FaChartBar,
  FaClock,
  FaMoneyCheck,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";

const Dashboard = () => {
  const {
    isSubadmin,
    isSubSubAdmin,
    isVendorAdmin,
    societyId: sessionSocietyId,
  } = useAuth();
  const [subadminName, setSubadminName] = useState(() => {
    try {
      return (
        sessionStorage.getItem("QV_SUBADMIN_NAME") ||
        sessionStorage.getItem("QV_NAME") ||
        sessionStorage.getItem("name") ||
        sessionStorage.getItem("subadminName") ||
        ""
      );
    } catch {
      return "";
    }
  });
  const [stats, setStats] = useState({
    totalVendors: 0,
    totalUsers: 0,
    totalBookings: 0,
    totalSubscriptions: 0,
    pendingKYC: 0,
    pendingVerifications: 0,
    unassignedBookings: 0, // Add this field
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchingRef = useRef(false);

  useEffect(() => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    fetchDashboardStats().finally(() => {
      fetchingRef.current = false;
    });
  }, []);

  // Redirect sub-sub admins to their specific dashboard
  useEffect(() => {
    if (isSubSubAdmin) {
      navigate("/subsubadmin/dashboard");
    }
  }, [isSubSubAdmin, navigate]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
      const base = rawBase.replace(/\/$/, "");

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const headers = {};
      if (adminToken) {
        headers["Authorization"] = `Bearer ${adminToken}`;
      }

      // High-performance single aggregated endpoint call
      const res = await fetch(`${base}/api/admins/dashboard-stats`, { headers });
      if (res.status === 401) {
        sessionStorage.clear();
        localStorage.removeItem("adminToken");
        window.location.href = "/admin";
        return;
      }

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.stats) {
          setStats(data.stats);
          setLoading(false);
          return;
        }
      }

      // Fallback in case of legacy API server
      const results = await Promise.allSettled([
        fetch(`${base}/api/vendors`, { headers }),
        fetch(`${base}/api/users`, { headers }),
        fetch(`${base}/api/bookings/summary`, { headers }),
      ]);

      const [vendorsRes, usersRes, summaryRes] = results.map((r) =>
        r.status === "fulfilled" && r.value && r.value.ok ? r.value : null
      );
      const vendorsJson = vendorsRes ? await vendorsRes.json() : null;
      const usersJson = usersRes ? await usersRes.json() : null;
      const summaryJson = summaryRes ? await summaryRes.json() : {};

      const vendorsArr = Array.isArray(vendorsJson) ? vendorsJson : vendorsJson?.vendors || [];
      const usersArr = Array.isArray(usersJson) ? usersJson : usersJson?.users || [];

      setStats({
        totalVendors: vendorsArr.length,
        totalUsers: usersArr.length,
        totalBookings: summaryJson.totalBookings || 0,
        unassignedBookings: summaryJson.unassignedBookings || 0,
        totalSubscriptions: 0,
        pendingKYC: 0,
        pendingVerifications: 0,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      setError("Failed to load dashboard statistics");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <h4>Error</h4>
        <p>{error}</p>
        <Button onClick={fetchDashboardStats} variant="outline-primary">
          Retry
        </Button>
      </div>
    );
  }

  if (isVendorAdmin) {
    return (
      <div className="dashboard-container p-4">
        <h1 className="mb-4">Vendor Verification Panel</h1>

        <Row className="mb-4">
          <Col md={6}>
            <Card className="text-center dashboard-card">
              <Card.Body>
                <FaStore size={40} className="text-primary mb-2" />
                <Card.Title>{stats.totalVendors}</Card.Title>
                <Card.Text>Total Vendors</Card.Text>
                <Button
                  variant="outline-primary"
                  onClick={() => navigate("/vendor-dashboard")}
                >
                  View Vendors
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="text-center dashboard-card">
              <Card.Body>
                <FaUserCheck size={40} className="text-warning mb-2" />
                <Card.Title>{stats.pendingVerifications}</Card.Title>
                <Card.Text>Pending Verifications</Card.Text>
                <Button
                  variant="outline-warning"
                  onClick={() => navigate("/vendor-verification-requests")}
                >
                  Review Requests
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Button
          variant="outline-danger"
          onClick={() => {
            sessionStorage.clear();
            localStorage.removeItem("adminToken");
            window.location.href = "/admin";
          }}
        >
          Logout
        </Button>
      </div>
    );
  }

  return (
    <Container fluid className="px-3 px-md-4 py-3">
      {(() => {
        if (isSubadmin) {
          return (
            <h2 className="mb-4 fw-bold">
              {subadminName ? `Welcome ${subadminName}` : "Welcome Sub Admin"}
            </h2>
          );
        }
        return <h2 className="mb-4 fw-bold">Admin Dashboard</h2>;
      })()}

      {isSubadmin ? (
        <Row className="g-3 g-md-4 mb-4">
          <Col xs={12} sm={6} md={4} lg={3}>
            <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
              <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                <div>
                  <FaStore className="text-primary mb-2" size={36} />
                  <Card.Title className="h3 fw-bold mb-1">{stats.totalVendors}</Card.Title>
                  <Card.Text className="text-muted small">Total Vendors</Card.Text>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="w-100 mt-2"
                  onClick={() => navigate("/vendor-dashboard")}
                >
                  <FaEye className="me-1" />
                  View All
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
              <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                <div>
                  <FaUsers className="text-success mb-2" size={36} />
                  <Card.Title className="h3 fw-bold mb-1">{stats.totalUsers}</Card.Title>
                  <Card.Text className="text-muted small">Total Users</Card.Text>
                </div>
                <Button
                  variant="outline-success"
                  size="sm"
                  className="w-100 mt-2"
                  onClick={() => navigate("/user-dashboard")}
                >
                  <FaEye className="me-1" />
                  View All
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
              <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                <div>
                  <FaCreditCard className="text-info mb-2" size={36} />
                  <Card.Title className="h3 fw-bold mb-1">{stats.totalSubscriptions}</Card.Title>
                  <Card.Text className="text-muted small">Local Subscriptions</Card.Text>
                </div>
                <Button
                  variant="outline-info"
                  size="sm"
                  className="w-100 mt-2"
                  onClick={() => navigate("/local-subscriptions")}
                >
                  <FaEye className="me-1" />
                  View All
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
              <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                <div>
                  <FaUser className="text-warning mb-2" size={36} />
                  <Card.Title className="h4 fw-bold mb-1">Society Admin</Card.Title>
                  <Card.Text className="text-muted small">Manage sub-sub admins</Card.Text>
                </div>
                <Button
                  variant="outline-warning"
                  size="sm"
                  className="w-100 mt-2"
                  onClick={() => navigate("/sub-sub-admins")}
                >
                  <FaEye className="me-1" />
                  View All
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <>
          <Row className="g-3 g-md-4 mb-4">
            <Col xs={12} sm={6} md={4} lg={3}>
              <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                  <div>
                    <FaStore className="text-primary mb-2" size={36} />
                    <Card.Title className="h3 fw-bold mb-1">{stats.totalVendors}</Card.Title>
                    <Card.Text className="text-muted small">Total Vendors</Card.Text>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100 mt-2"
                    onClick={() => navigate("/vendor-dashboard")}
                  >
                    <FaEye className="me-1" />
                    View All
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                  <div>
                    <FaUsers className="text-success mb-2" size={36} />
                    <Card.Title className="h3 fw-bold mb-1">{stats.totalUsers}</Card.Title>
                    <Card.Text className="text-muted small">Total Users</Card.Text>
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="w-100 mt-2"
                    onClick={() => navigate("/user-dashboard")}
                  >
                    <FaEye className="me-1" />
                    View All
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                  <div>
                    <FaUserCheck className="text-info mb-2" size={36} />
                    <Card.Title className="h3 fw-bold mb-1">{stats.pendingVerifications}</Card.Title>
                    <Card.Text className="text-muted small">Pending Verifications</Card.Text>
                  </div>
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="w-100 mt-2"
                    onClick={() => navigate("/vendor-verification-requests")}
                  >
                    <FaEye className="me-1" />
                    Review Now
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                  <div>
                    <FaShieldAlt className="text-danger mb-2" size={36} />
                    <Card.Title className="h3 fw-bold mb-1">{stats.pendingKYC}</Card.Title>
                    <Card.Text className="text-muted small">Pending KYC</Card.Text>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="w-100 mt-2"
                    onClick={() => navigate("/kyc-verification")}
                  >
                    <FaEye className="me-1" />
                    Review Now
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 g-md-4 mb-4">
            <Col xs={12} sm={6} md={4} lg={3}>
              <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                  <div>
                    <FaChartLine className="text-warning mb-2" size={36} />
                    <Card.Title className="h4 fw-bold mb-1">Sub Admin</Card.Title>
                    <Card.Text className="text-muted small">Manage sub admins</Card.Text>
                  </div>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    className="w-100 mt-2"
                    onClick={() => navigate("/sub-admins")}
                  >
                    <FaEye className="me-1" />
                    View All
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                  <div>
                    <FaHome className="text-danger mb-2" size={36} />
                    <Card.Title className="h4 fw-bold mb-1">Sub-Sub Admin</Card.Title>
                    <Card.Text className="text-muted small">Manage sub-sub admins</Card.Text>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="w-100 mt-2"
                    onClick={() => navigate("/sub-sub-admins")}
                  >
                    <FaEye className="me-1" />
                    View All
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                  <div>
                    <FaFolder className="text-primary mb-2" size={36} />
                    <Card.Title className="h4 fw-bold mb-1">Categories</Card.Title>
                    <Card.Text className="text-muted small">Categories & Packages</Card.Text>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100 mt-2"
                    onClick={() => navigate("/categories")}
                  >
                    <FaEye className="me-1" />
                    Manage
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                  <div>
                    <FaCreditCard className="text-info mb-2" size={36} />
                    <Card.Title className="h3 fw-bold mb-1">{stats.totalSubscriptions}</Card.Title>
                    <Card.Text className="text-muted small">Total Subscriptions</Card.Text>
                  </div>
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="w-100 mt-2"
                    onClick={() => navigate("/subscriptions")}
                  >
                    <FaEye className="me-1" />
                    View All
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Card className="text-center shadow-sm border-0 h-100 dashboard-card">
                <Card.Body className="d-flex flex-column align-items-center justify-content-between p-3">
                  <div>
                    <FaChartBar className="text-success mb-2" size={36} />
                    <Card.Title className="h4 fw-bold mb-1">Analytics</Card.Title>
                    <Card.Text className="text-muted small">Repeat Service Insights</Card.Text>
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="w-100 mt-2"
                    onClick={() => navigate("/repeat-service-analytics")}
                  >
                    <FaEye className="me-1" />
                    View Report
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      <Row className="g-3 g-md-4">
        <Col xs={12} lg={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-light border-0 py-3">
              <h5 className="mb-0 fw-bold">Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                {isSubadmin ? (
                  <>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => navigate("/refer-friends")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaUser className="me-2" />
                      Refer Friends
                    </Button>
                    <Button
                      variant="success"
                      size="lg"
                      onClick={() => navigate("/assigned-bookings")}
                      className="d-flex align-items-center justify-content-center py-3"
                      style={{
                        background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                        border: "none",
                        fontWeight: 600,
                      }}
                    >
                      <FaUserCheck className="me-2" />
                      Assigned Bookings
                    </Button>
                    {/* Unassigned Bookings Button for Subadmin */}
                    <Button
                      variant="warning"
                      size="lg"
                      onClick={() => navigate("/unassigned-bookings")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaClock className="me-2" />
                      Unassigned Bookings ({stats.unassignedBookings || 0})
                    </Button>
                    <Button
                      variant="warning"
                      size="lg"
                      onClick={() => navigate("/user-dashboard")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaUser className="me-2" />
                      User Dashboard
                    </Button>
                    <Button
                      variant="info"
                      size="lg"
                      onClick={() => navigate("/vendor-dashboard")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaStore className="me-2" />
                      Vendor Dashboard
                    </Button>
                    <Button
                      variant="success"
                      size="lg"
                      onClick={() => navigate("/vendor-verification-requests")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaUserCheck className="me-2" />
                      Vendor Verification Requests
                    </Button>
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={() => navigate("/local-subscriptions")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaCreditCard className="me-2" />
                      Local Subscriptions
                    </Button>
                    <Button
                      variant="info"
                      size="lg"
                      onClick={() => navigate("/ads-management")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaAd className="me-2" />
                      Ads Management
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="info"
                      size="lg"
                      onClick={() => navigate("/vendor-dashboard")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaStore className="me-2" />
                      Vendor Dashboard
                    </Button>
                    <Button
                      variant="success"
                      size="lg"
                      onClick={() => navigate("/assigned-bookings")}
                      className="d-flex align-items-center justify-content-center py-3"
                      style={{
                        background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                        border: "none",
                        fontWeight: 600,
                      }}
                    >
                      <FaUserCheck className="me-2" />
                      Assigned Bookings
                    </Button>
                    <Button
                      variant="success"
                      size="lg"
                      onClick={() => navigate("/user-dashboard")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaUser className="me-2" />
                      User Dashboard
                    </Button>
                    {/* Unassigned Bookings Button for Admin */}
                    <Button
                      variant="warning"
                      size="lg"
                      onClick={() => navigate("/unassigned-bookings")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaClock className="me-2" />
                      Unassigned Bookings ({stats.unassignedBookings || 0})
                    </Button>
                    <Button
                      variant="warning"
                      size="lg"
                      onClick={() => navigate("/refer-friends")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaUser className="me-2" />
                      Refer Friends
                    </Button>
                    <Button
                      variant="dark"
                      size="lg"
                      onClick={() => navigate("/api-settings")}
                      className="d-flex align-items-center justify-content-center py-3"
                      style={{
                        background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
                        border: "none",
                        fontWeight: 600,
                      }}
                    >
                      <FaCog className="me-2" />
                      API & Integration Settings
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => navigate("/subscription-management")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaCreditCard className="me-2" />
                      Subscription Plans
                    </Button>
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={() => navigate("/transactions-management")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaMoneyBillWave className="me-2" />
                      Transactions
                    </Button>
                    <Button
                      variant="danger"
                      size="lg"
                      onClick={() => navigate("/withdrawal-requests")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaMoneyCheck className="me-2" />
                      Withdrawal Requests
                    </Button>
                    <Button
                      variant="dark"
                      size="lg"
                      onClick={() => navigate("/ads-management")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaAd className="me-2" />
                      Ads Management
                    </Button>
                    <Button
                      variant="info"
                      size="lg"
                      onClick={() => navigate("/surveys-management")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaChartBar className="me-2" />
                      Survey Management
                    </Button>
                    <Button
                      variant="success"
                      size="lg"
                      onClick={() => navigate("/complaints-management")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaChartBar className="me-2" />
                      Complaint Management
                    </Button>
                    <Button
                      variant="danger"
                      size="lg"
                      onClick={() => navigate("/repeat-service-analytics")}
                      className="d-flex align-items-center justify-content-center py-3"
                    >
                      <FaChartLine className="me-2" />
                      Repeat Service Analytics
                    </Button>
                  </>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Header className="bg-light border-0 py-3">
              <h5 className="mb-0 fw-bold">System Information</h5>
            </Card.Header>
            <Card.Body className="d-flex flex-column justify-content-between">
              <div>
                <div className="d-flex align-items-center mb-3">
                  <FaHome className="text-primary me-2 flex-shrink-0" size={20} />
                  <div>
                    <div className="fw-semibold">System</div>
                    <div className="text-muted small">
                      QuiVikme Admin Panel ensures safe and dependable management
                      operations.
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <FaUserCheck className="text-success me-2 flex-shrink-0" size={20} />
                  <div>
                    <div className="fw-semibold">User Role</div>
                    <div className="text-muted small">
                      {isSubadmin ? "Sub Admin" : "Administrator"}
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <FaCog className="text-info me-2 flex-shrink-0" size={20} />
                  <div>
                    <div className="fw-semibold">System Status</div>
                    <div className="text-muted small">Operational</div>
                  </div>
                </div>
              </div>
              <div className="d-grid gap-2 mt-4">
                <Button
                  variant="outline-danger"
                  onClick={() => {
                    sessionStorage.clear();
                    localStorage.removeItem("adminToken");
                    window.location.href = "/admin";
                  }}
                >
                  <FaUser className="me-2" />
                  Logout
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;